import React, { useState, useEffect } from 'react';
import {
    Input,
    Form,
    FormGroup,
    Label,
    Button,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Table,
    UncontrolledCollapse,
    Row,
    Col,
} from 'reactstrap';
import classes from './ConsentManager.module.scss';
import { IconChevronDown } from '@tabler/icons-react';
import Cookies from 'js-cookie';
import axios from '../../http/index.js';
import PageBlockRenderer from '../Page/PageBlockRenderer';
import HTMLBlock from '../Page/HTMLBlock';

/**
 * Cookie names of the "necessary" category. They all share the product prefix
 * (`product.consentCookiePrefix`), exactly like the names the consent handlers
 * below set. Listing them literally would print Bigfoot's names on Cassiopeia,
 * where the cookies actually set are `cassiopeia_*`.
 */
const CONSENT_COOKIE_SUFFIXES = [
    'cookie-consent',
    'functional-consent',
    'statistic-consent',
    'personalization-consent',
    'code-requested',
    'tour-projects',
];

/**
 * Default consent texts and categories. Campaigns override individual fields through the
 * `consentConfig` prop; the defaults reproduce the wording both frontends shipped so far.
 * The cookie table is derived from the product prefix, so there is one source for the names.
 */
export const createConsentConfig = (cookiePrefix) => ({
    headline: 'Ihre Datenschutzpräferenzen',
    skipLinks: [
        { href: '#cookie-settings', label: 'Zu den Cookie-Einstellungen springen', className: 'btn btn-link' },
        { href: '#privacy-policy', label: 'Zur Privatssphäreerklärung springen', className: 'btn btn-link ms-2' },
        { href: '#consent-actions', label: 'Zu den Zustimmungsaktionen springen', className: 'btn btn-link ms-2' },
    ],
    tabs: {
        cookies: { label: 'Cookies', className: 'anl_cmt--tab--cookies' },
        privacy: { label: 'Datenschutzerklärung', className: 'anl_cmt--tab--privacy' },
    },
    intro: 'Wir setzen Cookies und andere Technologien ein, um Ihnen das bestmögliche Nutzungs\u00ADerlebnis zu gewährleisten. Im Folgenden haben Sie die Möglichkeit Ihre persönlichen Daten\u00ADschutz\u00ADpräferenzen festzulegen.',
    categories: [
        {
            id: 'necessaryCheck',
            togglerId: 'togglerNecessary',
            inputName: 'Notwendig',
            label: 'Notwendig',
            required: true,
            descriptionClassName: 'text-muted mb-1 p-2',
            description:
                'Notwendige Technologien sind zur Funktion unserer Website erforderlich und können nicht deaktiviert werden. Cookies dieser Kategorie werden als Reaktion auf von Ihnen getätigten Aktionen gesetzt, die einer Dienstanforderung entsprechen, wie etwa dem Festlegen Ihrer Datenschutzeinstellungen.',
            details: {
                anchorId: 'bigfoot-app',
                className: 'anl_cmt--details--bigfoot-app',
                label: 'Details zur Weiterverarbeitung Ihrer Daten finden Sie hier',
            },
            table: {
                service: 'Voting-Wettbewerb',
                provider: 'Social Value GmbH',
                data: 'Ihre Nutzerpräferenzen',
                cookies: CONSENT_COOKIE_SUFFIXES.map((suffix) => `${cookiePrefix}_${suffix}`),
                basis: 'Art. 6 Abs. 1 s. 1 lit. c DSGVO',
                location: 'Paris (Frankreich)',
            },
        },
        {
            id: 'statisticCheck',
            togglerId: 'togglerStatistic',
            inputName: 'statisticCheck',
            label: 'Statistik',
            required: false,
            descriptionClassName: 'mb-1 p-2',
            description:
                'Mit diesen Cookies können wir Besuche zählen und Zugriffs\u00ADquellen eruieren, um die Leistung unserer Website zu ermitteln und zu verbessern.',
            details: {
                anchorId: 'etracker-web-analyse',
                className: 'anl_cmt--details--etracker-web-analytics',
                label: 'Details zur Weiterverarbeitung Ihrer Daten finden Sie hier',
            },
            table: {
                service: 'etracker Webanalyse',
                provider: 'etracker GmbH',
                data: 'Besuchte Seiten, Endgerät, Browser, Betriebssystem, Referrer, Nutzerinteraktionen, IP-Adresse',
                cookies: ['BT_pdc', 'BT_sdc', 'et_oip', '_et_coid', 'isSdEnabled'],
                basis: 'Einwilligung, Art. 6 Abs. 1 lit. a DSGVO',
                location: 'Europäische Union',
            },
        },
    ],
    privacyPolicySlug: 'datenschutz',
    actions: {
        acceptSelection: { label: 'Auswahl akzeptieren', className: 'anl_cmt--btn--accept-selection' },
        acceptAll: { label: 'Alles akzeptieren', className: 'anl_cmt--btn--accept-all' },
    },
});

/** Service table of one consent category; the first row carries the highlight class. */
const ConsentCategoryTable = ({ category, onDetailsClick }) => (
    <Table size="sm" borderless>
        <tbody>
            <tr className={classes.ConsentManagerTable}>
                <th className="w-25" scope="row">
                    Dienst
                </th>
                <td className="px-0">{category.table.service}</td>
            </tr>
            <tr>
                <th className="w-25" scope="row">
                    Anbieter
                </th>
                <td className="px-0">{category.table.provider}</td>
            </tr>
            <tr>
                <th className="w-25" scope="row">
                    Daten
                </th>
                <td className="px-0">{category.table.data}</td>
            </tr>
            <tr>
                <th className="w-25" scope="row">
                    Cookies
                </th>
                <td className="px-0 text-monospace">
                    {category.table.cookies.map((cookieName) => (
                        <span key={cookieName} className="me-1 badge rounded-pill text-bg-warning">
                            {cookieName}
                        </span>
                    ))}
                </td>
            </tr>
            <tr>
                <th className="w-25" scope="row">
                    Grundlage
                </th>
                <td className="px-0">{category.table.basis}</td>
            </tr>
            <tr>
                <th className="w-25" scope="row">
                    Verarbeitungsort
                </th>
                <td className="px-0">{category.table.location}</td>
            </tr>
            <tr>
                <th className="w-25" scope="row">
                    Verarbeitung Ihrer Daten
                </th>
                <td className="px-0">
                    <button
                        type="button"
                        onClick={() => onDetailsClick(category.details.anchorId)}
                        className={`btn btn-link p-0 ${category.details.className}`}
                    >
                        {category.details.label}
                    </button>
                </td>
            </tr>
        </tbody>
    </Table>
);

const ConsentManager = ({ pages, consentCallback, cookiePrefix = 'bigfoot', consentConfig }) => {
    const config = { ...createConsentConfig(cookiePrefix), ...consentConfig };

    const [activeTab, setActiveTab] = useState('1');
    const [switchTab, setSwitchTab] = useState(false);
    const [statisticCheck, setStatisticCheck] = useState(Cookies.get(`${cookiePrefix}_statistic-consent`) === 'true');
    const [personalizationCheck, setPersonalizationCheck] = useState(
        Cookies.get(`${cookiePrefix}_personalization-consent`) === 'true'
    );
    const [performanceCheck, setPerformanceCheck] = useState(
        Cookies.get(`${cookiePrefix}_performance-consent`) === 'true'
    );
    const [linkAll, setLinkAll] = useState(false);
    const [clientLoad, setClientLoad] = useState(null);
    // clientAction is sent to the API on consent submission; it is not rendered but must be tracked in state.
    // eslint-disable-next-line no-unused-vars
    const [clientAction, setClientAction] = useState(null);
    const [scrollToHeadline, setScrollToHeadline] = useState(null);

    const tabHandler = (tab, scrollTarget) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setSwitchTab(true);
        }
    };

    const inputChangeHandler = (event) => {
        const target = event.target;
        const name = target.id;
        const checked = target.checked;

        if (name === 'statisticCheck') setStatisticCheck(checked);
        if (name === 'personalizationCheck') setPersonalizationCheck(checked);
        if (name === 'performanceCheck') setPerformanceCheck(checked);
    };

    const selectionConsentHandler = async () => {
        let date = Date.now();
        setClientAction(date);
        Cookies.set(`${cookiePrefix}_cookie-consent`, true, { expires: 180 });

        const stateToPass = {
            switchTab,
            activeTab,
            statisticCheck,
            personalizationCheck,
            performanceCheck,
            linkAll,
            clientLoad,
            clientAction: date,
        };

        consentCallback(stateToPass);
        axios
            .post('/analytics/', stateToPass)
            .then((response) => {
                console.debug(response);
                window.location.reload(true);
            })
            .catch((error) => {
                console.debug(error);
            });
    };

    const completeConsentHandler = async () => {
        let date = Date.now();
        setStatisticCheck(true);
        setPersonalizationCheck(true);
        setPerformanceCheck(true);
        setLinkAll(true);
        setClientAction(date);

        Cookies.set(`${cookiePrefix}_cookie-consent`, true, { expires: 180 });

        const stateToPass = {
            activeTab,
            switchTab,
            statisticCheck: true,
            personalizationCheck: true,
            performanceCheck: true,
            linkAll: true,
            clientLoad,
            clientAction: date,
        };

        consentCallback(stateToPass);
        axios
            .post('/analytics/', stateToPass)
            .then((response) => {
                console.debug(response);
                window.location.reload(true);
            })
            .catch((error) => {
                console.debug(error);
            });
    };

    const handleLinkClick = (headlineId) => {
        tabHandler('2');
        setScrollToHeadline(headlineId);
    };

    const scrollIfRequired = () => {
        if (scrollToHeadline) {
            setTimeout(() => {
                const headlineElement = document.getElementById(scrollToHeadline);

                if (headlineElement) {
                    headlineElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest',
                    });
                    setScrollToHeadline(null);
                }
            }, 100);
        }
    };

    useEffect(() => {
        let date = Date.now();
        setClientLoad(date);
    }, []);

    useEffect(() => {
        scrollIfRequired();
        // Including scrollIfRequired would cause an infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollToHeadline]);

    let privacyPolicyPage = null;
    let privacyPolicyPageMarkup = null;

    if (pages) {
        privacyPolicyPage = pages.find((page) => page.slug === config.privacyPolicySlug);
        if (privacyPolicyPage) privacyPolicyPageMarkup = privacyPolicyPage.body;
    }

    return (
        <React.Fragment>
            <ModalHeader tag="div" className="border-bottom-0">
                <ul className="visually-hidden-focusable mb-3">
                    {config.skipLinks.map((skipLink) => (
                        <li key={skipLink.href}>
                            <a href={skipLink.href} className={skipLink.className}>
                                {skipLink.label}
                            </a>
                        </li>
                    ))}
                </ul>
                <h4>{config.headline}</h4>
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            tag="button"
                            className={config.tabs.cookies.className}
                            onClick={() => tabHandler('1')}
                            active={activeTab === '1'}
                        >
                            {config.tabs.cookies.label}
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            tag="button"
                            className={config.tabs.privacy.className}
                            onClick={() => tabHandler('2')}
                            active={activeTab === '2'}
                        >
                            {config.tabs.privacy.label}
                        </NavLink>
                    </NavItem>
                </Nav>
            </ModalHeader>
            <ModalBody className="pt-1">
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                        <p id="cookie-settings">{config.intro}</p>
                        <Form>
                            {config.categories.map((category) => (
                                <FormGroup key={category.id} switch className="form-group mb-2 px-1 rounded bg-light">
                                    <Row className="p-2 align-content-center">
                                        <Col id={category.togglerId}>
                                            <Label
                                                for={category.id}
                                                className="align-content-center mb-0"
                                                id={`${category.id}Label`}
                                            >
                                                <IconChevronDown size={24} className="text-primary d-inline me-2" />
                                                <h5 className="m-0 text-primary d-inline">
                                                    {category.label}
                                                    <span className="visually-hidden">
                                                        (Auswählen um die Details aufzuklappen)
                                                    </span>
                                                </h5>
                                            </Label>
                                        </Col>
                                        <Col xs="auto">
                                            <Input
                                                name={category.inputName}
                                                id={category.id}
                                                onChange={category.required ? undefined : inputChangeHandler}
                                                type="switch"
                                                role="switch"
                                                aria-labelledby={`${category.id}Label`}
                                                checked={category.required ? true : statisticCheck}
                                                disabled={category.required || undefined}
                                                onClick={
                                                    category.required
                                                        ? undefined
                                                        : () => setStatisticCheck(!statisticCheck)
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    <UncontrolledCollapse toggler={`#${category.togglerId}`}>
                                        <p className={category.descriptionClassName}>{category.description}</p>
                                        <ConsentCategoryTable category={category} onDetailsClick={handleLinkClick} />
                                    </UncontrolledCollapse>
                                </FormGroup>
                            ))}
                        </Form>
                    </TabPane>
                    <TabPane tabId="2" id="privacy-policy">
                        {privacyPolicyPageMarkup ? (
                            Array.isArray(privacyPolicyPageMarkup) ? (
                                <PageBlockRenderer blocks={privacyPolicyPageMarkup} />
                            ) : typeof privacyPolicyPageMarkup === 'string' ? (
                                <HTMLBlock html={privacyPolicyPageMarkup} />
                            ) : null
                        ) : (
                            'Loading'
                        )}
                        <div />
                    </TabPane>
                </TabContent>
            </ModalBody>
            <ModalFooter>
                <div id="consent-actions" />
                <Button
                    className={config.actions.acceptSelection.className}
                    onClick={selectionConsentHandler}
                    outline
                    color="dark"
                    block
                >
                    {config.actions.acceptSelection.label}
                </Button>
                <Button
                    className={config.actions.acceptAll.className}
                    onClick={completeConsentHandler}
                    color="secondary"
                    block
                >
                    {config.actions.acceptAll.label}
                </Button>
            </ModalFooter>
        </React.Fragment>
    );
};

export default ConsentManager;
